<template>
  <div
    v-if="pageLoading"
    class="m-3"
  >
    <placeholder-content :is-load="true" />
  </div>
  <div
    v-else-if="!hasPermission"
    class="m-3"
  >
    <access-denied/>
  </div>
  <div
    v-else
  >
    <div class="d-flex justify-content-between border-bottom mb-3">
      <ul class="nav nav-custom">
        <li class="nav-item">
          <a class="nav-link active">
            {{ $t('All') }}
            <span class="badge rounded-pill bg-primary">
              {{ datatable.total }}
            </span>
          </a>
        </li>
      </ul>
      <column-toggle
        :headers="allHeaders"
        storage-key="kds-stations.columns"
        v-model="visibleColumns"
      />
    </div>
    <div class="d-flex justify-content-start mx-3 flex-column flex-sm-row">
      <!-- โควตาจอครัวตามแพ็ก — แพตเทิร์นเดียวกับปุ่มสร้างสาขา (branches/index.vue)
           used = สถานีที่สร้างเอง ไม่นับหน้ารวม → แพ็ก limit 0 = มีแค่หน้ารวม ปุ่มปิดตั้งแต่ยังไม่มีสถานี (ไม่โชว์ badge 0/0)
           title อยู่ที่ div เพราะ tooltip ของปุ่ม disabled ไม่โชว์
           ⚠️ ตัวบังคับจริงคือ limit:limit_kds_stations ฝั่ง API -->
      <div
        class="btn-group btn-group-sm me-sm-2 mb-3"
        :title="stationQuotaTitle"
      >
        <button
          type="button"
          class="btn btn-sm btn-primary"
          :disabled="stationQuotaFull"
          @click="() => {
            offcanvasFroms.open = true;
            offcanvasFroms.type = 'CREATE';
          }"
        >
          <i class="fa-solid fa-circle-plus me-2" />
          {{ $t('Create') }}
          <span
            v-if="hasStationLimit && stationLimit > 0"
            class="badge bg-white text-primary ms-2"
          >
            {{ stationUsed }}/{{ stationLimit }}
          </span>
        </button>
      </div>

      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          aria-label="Search"
          v-model.trim="params.search"
          @keyup.enter="fetchData"
        >
        <button
          class="input-group-text"
          type="button"
          @click="fetchData"
        >
          <i class="fas fa-search" />
        </button>
      </div>

      <button
        type="button"
        class="btn btn-sm btn-outline-secondary me-sm-2 mb-3"
        @click="offcanvasFilter.open = true"
      >
        <i class="fa-solid fa-sliders me-1" />
        {{ $t('Filters') }}
        <span v-if="totalActiveFilters" class="badge rounded-pill bg-primary ms-1">
          {{ totalActiveFilters }}
        </span>
      </button>

      <per-page
        @per-page="setPerPage"
      />
    </div>
    <vue3-easy-data-table
      v-model:server-options="datatable.serverOptions"
      ref="dataTable"
      :headers="visibleHeaders"
      :items="datatable.items"
      :loading="datatable.loading"
      :server-items-length="datatable.total"
      :theme-color="'#515151'"
      :hide-footer="false"
      :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')"
      :table-min-height="null"
      buttons-pagination
      :body-item-class-name="bodyItemClassNameFunction"
      table-class-name='customize-table'
    >
      <template #item-name="item">
        <span
          class="d-inline-block rounded-circle me-2 align-middle"
          style="width: 12px; height: 12px;"
          :style="{ backgroundColor: item.color || '#9ca3af' }"
        />
        <span class="fw-semibold align-middle">{{ $t(item.name) }}</span>
      </template>
      <template #item-branch="item">
        {{ item.branch && item.branch.name ? $t(item.branch.name) : '-' }}
      </template>
      <template #item-categories="item">
        <span
          v-for="cat in item.categories"
          :key="cat.id"
          class="badge bg-light text-dark border me-1 mb-1"
        >
          {{ $ln(cat) }}
        </span>
        <span v-if="!item.categories || !item.categories.length" class="text-muted">-</span>
      </template>
      <template #item-is_active="item">
        <span v-if="item.is_active" class="badge bg-success">
          <i class="fas fa-check-circle me-1" />
          {{ $t('Active') }}
        </span>
        <span v-else class="badge bg-secondary">
          <i class="fas fa-ban me-1" />
          {{ $t('Inactive') }}
        </span>
      </template>
      <template #item-action="item">
        <div class="icon-link custom-actions">
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => {
                offcanvasFroms.open = true;
                offcanvasFroms.id = item.id;
                offcanvasFroms.type = 'EDIT';
              }"
            >
              <i class="fa-solid fa-square-pen" />
            </button>
            <template #content>
              <div>{{ $t('Edit') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="handleDelete(item.id)"
            >
              <i class="fa-solid fa-trash-can" />
            </button>
            <template #content>
              <div>{{ $t('Delete') }}</div>
            </template>
          </popper>
        </div>
      </template>
      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>

    <offcanvas-form
      v-if="offcanvasFroms.open"
      :type="offcanvasFroms.type"
      :id="offcanvasFroms.id"
    />
    <offcanvas-filter
      v-if="offcanvasFilter.open"
      :sections="filterSections"
      :value="{ branch_ids: params.branch_ids }"
      @apply="onApplyFilter"
      @close="offcanvasFilter.open = false"
    />
  </div>
</template>
<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import PerPage from '@/plugins/PerPage.vue';
import FieldsDef from "./assets/template/datatable/FieldsDef";
import OffcanvasForm from './OffcanvasForm.vue';
import OffcanvasFilter from '@/components/shared/OffcanvasFilter.vue';
import ColumnToggle from '@/components/shared/ColumnToggle.vue';
import { loadVisibleColumns } from '@/utils/columnToggle';
import { KdsStations } from '@/services/endpoint/kds-stations.service';
import moment from "moment";
import Popper from "vue3-popper";

export default {
  data() {
    return {
      params: {
        limit: 10,
        page: 1,
        search: '',
        branch_ids: []
      },
      pageLoading: false,
      hasPermission: false,
      // จาก API (limit_station / used_station) · limit null = ไม่จำกัด · used ไม่นับหน้ารวม
      stationLimit: null,
      stationUsed: 0,
      filters: {
        branches: []
      },
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10
        }
      },
      offcanvasFroms: {
        open: false,
        type: 'CREATE',
        id: 0
      },
      offcanvasFilter: {
        open: false
      },
      visibleColumns: loadVisibleColumns('kds-stations.columns', FieldsDef(t => t))
    }
  },
  components: {
    PlaceholderContent,
    AccessDenied,
    PerPage,
    Vue3EasyDataTable,
    OffcanvasForm,
    OffcanvasFilter,
    ColumnToggle,
    Popper
  },
  computed: {
    hasStationLimit() {
      return this.stationLimit !== null && this.stationLimit !== undefined;
    },
    stationQuotaFull() {
      return this.hasStationLimit && this.stationUsed >= this.stationLimit;
    },
    stationQuotaTitle() {
      if (!this.stationQuotaFull) return '';
      // limit 0 = หน้ารวมอย่างเดียว — บอกตรง ๆ ว่าแพ็กนี้ไม่มีสถานีแยก ไม่ใช่ "ครบแล้ว"
      return this.stationLimit === 0
        ? this.$t('_plan.This plan includes only the overview screen')
        : this.$t('_plan.KDS station limit reached');
    },
    allHeaders() {
      return this.datatable.headers(this.$t);
    },
    visibleHeaders() {
      return this.allHeaders.filter(h => !h.togglable || this.visibleColumns.includes(h.value));
    },
    totalActiveFilters() {
      return this.params.branch_ids.length;
    },
    filterSections() {
      return [
        {
          key: 'branch_ids',
          label: 'Branch',
          icon: 'fa-store',
          options: this.filters.branches
        }
      ];
    }
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'align-top py-1 px-2';
      const columnEnd = ['created_by'];
      if (columnEnd.includes(column)) {
        className += ' text-end';
      }
      return className;
    },
    async fetchData() {
      this.datatable.loading = true;
      var queryString = '?' + $.param(this.params);
      await KdsStations.list(queryString).then(res => {
        this.datatable.items = res.data.data;
        this.datatable.total = res.data.meta.total;
        this.filters.branches = res.data.filter?.branches || [];
        // ห้ามใช้ meta.total — ตอนค้นหา/กรองสาขามันคือจำนวนที่ตรงเงื่อนไข ไม่ใช่ทั้งร้าน
        this.stationLimit = res.data.limit_station === undefined ? null : res.data.limit_station;
        this.stationUsed = res.data.used_station ?? 0;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
      });

      this.datatable.loading = false;
      return;
    },
    onApplyFilter(payload) {
      this.params.branch_ids = payload.branch_ids || [];
      this.offcanvasFilter.open = false;
      this.fetchData();
    },
    setPerPage(perPage) {
      this.params.limit = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
      this.fetchData();
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    async handleDelete(id) {
      try {
        let self = this;
        let title = this.$t("Delete this Station") + '?';
        let html = this.$t("You will not be able to undo this action.");
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return KdsStations.delete(id)
              .then((response) => {
                self.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Success!'));
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
  },
  watch: {
    "datatable.serverOptions" (nval, oval) {
      delete this.params.sort;
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      if (nval.sortBy && nval.sortType) this.params.sort = nval.sortBy + "|" + nval.sortType;
      this.fetchData();
    }
  }
}
</script>
